import { useMemo } from 'react';

import { useStores } from '../../hooks';
import { OpenOptions, OPTIONS_LIST, OPTIONS_LIST_WITH_API_PROXY } from '../../../config/openOptions';
import { SMALL_FILE_LIMIT } from '../../../stores/UploadFilesStore/constants';
import { FileMenu, FileOptionType } from '../../../types/types';
import { FilesListType } from '../../../stores';

// Large files are uploaded as multipart (see UploadFilesStore: isSinglePart = hasNASStorage || size < SMALL_FILE_LIMIT).
// The backend eventually drops `open_with` for these, but only after it finishes indexing them, which causes the
// "Open" button to flicker in right after upload and then vanish. hasNASStorage mirrors HAS_API_PROXY, so we can
// decide up front and never show "Open" for large multipart files.
export const isLargeMultipartFile = (fileSize: number, hasApiProxy: boolean): boolean => (
    !hasApiProxy && fileSize >= SMALL_FILE_LIMIT
);

// Always hidden in Share-with-Me regardless of role.
// 'revoke' is not currently emitted by the backend, but is kept here as a defensive
// filter against the value appearing in the FileOptionType union.
const HIDE_ON_SHARED_ALWAYS: FileOptionType[] = [
    'revoke',
];

// Hidden in Share-with-Me only when the user does NOT have manage_access on the file.
// Workspace co-owners (and direct co-owners) inherit manage_access from the workspace,
// so backend emits `menu.edit_shares = true` for them and we should NOT strip these.
// Backend is the source of truth (it only emits these options for owner/co-owner);
// the FE filter is defense-in-depth across the BE+FE rollout window.
const HIDE_ON_SHARED_UNLESS_MANAGE_ACCESS: FileOptionType[] = [
    'delete',
    'delete_file',
];

const HIDDEN_OPTIONS: FileOptionType[] = [
    'set_policy',
    'set_file_policy',
];

const getOpenWith = (options: OpenOptions[], HAS_API_PROXY: boolean): OpenOptions[] => {
    let result = [];
    if (options) {
        const allowedMethods: string[] = HAS_API_PROXY ? OPTIONS_LIST_WITH_API_PROXY : OPTIONS_LIST;
        result = options.filter((value) => allowedMethods.includes(value));
    }
    result.reverse();
    return result;
};

export const useOpenWithToDisplay = (
    openWith: OpenOptions[],
    HAS_API_PROXY: boolean,
): OpenOptions[] => useMemo<OpenOptions[]>(
    () => getOpenWith(openWith, HAS_API_PROXY),
    [openWith, HAS_API_PROXY],
);

export const useOptionsToDisplay = (
    options: FileOptionType[],
    isShared: boolean,
    hasManageAccess: boolean = false,
): FileOptionType[] => useMemo<FileOptionType[]>(
    () => {
        const reversedList = [...options].reverse()
            .filter((option) => !HIDDEN_OPTIONS.includes(option));
        if (!isShared) {
            return reversedList;
        }
        return reversedList.filter((option) => {
            if (HIDE_ON_SHARED_ALWAYS.includes(option)) {
                return false;
            }
            if (!hasManageAccess && HIDE_ON_SHARED_UNLESS_MANAGE_ACCESS.includes(option)) {
                return false;
            }
            return true;
        });
    },
    [options, isShared, hasManageAccess],
);

interface MenuToDisplay {
    openWith: OpenOptions[];
    options: FileOptionType[];
    hasShare: boolean;
    hasCopyLink: boolean;
    hasManageAccess: boolean;
}

/*
* Note: you need to wrap component into mobx.observer HOC in order to use this hook
* */
export const useMenu = (menu: Partial<FileMenu>, filesListType?: FilesListType, fileSize = 0): MenuToDisplay => {
    const {
        authSettingsStore: { HAS_API_PROXY },
        userStore,
    } = useStores();

    const isShared = filesListType === 'sharedFiles';
    const hasManageAccess = !!menu?.edit_shares || userStore.isConfirmedAdmin;

    const hideOpen = isLargeMultipartFile(fileSize, HAS_API_PROXY);
    const openWith = useOpenWithToDisplay(hideOpen ? [] : (menu?.open_with || []), HAS_API_PROXY);
    const options = useOptionsToDisplay(menu?.options || [], isShared, hasManageAccess);

    return {
        options,
        openWith,
        hasShare: !!menu?.share,
        hasCopyLink: !!menu?.copy_link && !menu?.share,
        hasManageAccess,
    };
};
